import { Component, OnInit, ViewChild } from '@angular/core';
import {
  ExcelExportService,
  FilterService,
  GridComponent,
  GridModule,
  PageService,
  SearchService,
  ToolbarService
} from '@syncfusion/ej2-angular-grids';
import { combineLatest, map, Observable, of } from 'rxjs';
import { MappingPendingCheck } from './mapping-pending-check.model';
import { AsyncPipe } from '@angular/common';
import { MappingsService } from './mappings.service';
import {
  ID_REJECT_MAPPING,
  ID_VALIDATE_MAPPING,
  TOOLBAR_ITEM_REJECT_MAPPING,
  TOOLBAR_ITEM_VALIDATE_MAPPING
} from '../shared/constants';
import { ClickEventArgs } from '@syncfusion/ej2-angular-navigations';
import { EaccessAuthService } from '../auth/eaccess-auth.service';
import { ToastComponent } from '../shared/toast.component';
import { SpinnerComponent } from '../shared/spinner.component';
import { CigpStaffAvatarCardComponent } from '../cigp-staff/cigp-staff-avatar-card.component';
import { MappingTypePipe } from './mapping-type.enum';

@Component({
  selector: 'eaa-mappings-pending-check',
  providers: [ExcelExportService, FilterService, PageService, SearchService, ToolbarService],
  imports: [AsyncPipe, CigpStaffAvatarCardComponent, GridModule, MappingTypePipe, SpinnerComponent, ToastComponent],
  template: `
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center gap-3">
          <h5 class="card-title m-0">MAPPINGS PENDING CHECK</h5>
        </div>
      </div>
      <div class="card-body p-0">
        <ejs-grid
          #grdMappingsPendingCheck
          id="grdMappingsPendingCheck"
          [dataSource]="mappingsPendingCheck$ | async"
          [allowPaging]="true"
          [pageSettings]="{ pageSize: 50, pageSizes: ['10', '20', '50', '100', '250', 'All'] }"
          [allowFiltering]="true"
          [filterSettings]="{ enableCaseSensitivity: false, ignoreAccent: true, type: 'Excel' }"
          [allowExcelExport]="true"
          [allowTextWrap]="true"
          [toolbar]="[toolbarItemValidateMapping, toolbarItemRejectMapping, 'Search']"
          (toolbarClick)="toolbarClick($event)"
          [selectionSettings]="{ persistSelection: true }"
          (rowSelected)="onRowSelected($event)"
          (rowDeselected)="onRowSelected($event)"
        >
          <e-columns>
            <e-column type="checkbox" width="40" />
            <e-column field="id" headerText="" [visible]="false" [isPrimaryKey]="true" />
            <e-column field="mappingId" headerText="#" textAlign="Right" width="80" format="N0" />
            <e-column field="accountNumber" headerText="Account" />
            <e-column field="mappingType" headerText="Type">
              <ng-template #template let-data>
                <div>
                  {{ data.mappingType | mappingType }}
                </div>
              </ng-template>
            </e-column>
            <e-column field="clientEmail" headerText="Client">
              <ng-template #template let-data>
                <div [title]="data.clientEmail">
                  {{ data.client?.displayName || data.clientEmail }}
                </div>
              </ng-template>
            </e-column>
            <e-column field="cigpStaffEmail" headerText="CIGP Staff">
              <ng-template #template let-data>
                <eaa-cigp-staff-avatar-card [user]="data.cigpStaff" [showInitials]="true" />
              </ng-template>
            </e-column>
            <e-column
              field="submitted"
              headerText="Submitted"
              type="datetime"
              format="dd/MM/yyyy HH:mm:ss zz"
              width="200px"
            />
            <e-column field="submittedBy" headerText="Submitter" />
          </e-columns>
        </ejs-grid>
      </div>
    </div>

    <eaa-toast #toastValidateMappingsAction [showCloseButton]="false">
      <div id="title">PROCESSING</div>
      <div id="content" class="d-flex gap-2">
        <eaa-spinner [diameter]="20" />
        <div>
          {{ processingMessage }}
        </div>
      </div>
    </eaa-toast>
  `
})
export class MappingsPendingCheckComponent implements OnInit {
  public mappingsPendingCheck$: Observable<MappingPendingCheck[]> | null = null;
  public toolbarItemValidateMapping = TOOLBAR_ITEM_VALIDATE_MAPPING;
  public toolbarItemRejectMapping = TOOLBAR_ITEM_REJECT_MAPPING;
  private userEmail: string = '';
  public processingMessage = '';

  @ViewChild('grdMappingsPendingCheck')
  public grid?: GridComponent;

  @ViewChild('toastValidateMappingsAction')
  public toastValidateMappingsAction?: ToastComponent;

  constructor(
    private mappingsService: MappingsService,
    private authService: EaccessAuthService
  ) {}

  public ngOnInit(): void {
    this.mappingsPendingCheck$ = this.mappingsService.listMappingsPendingCheck$();

    this.authService.getActiveAccount$().subscribe((account) => {
      this.userEmail = account?.username || '';
    });
  }

  public onRowSelected(args: any): void {
    const selectedRecords = (this.grid?.getSelectedRecords() as MappingPendingCheck[]) || [];
    this.grid?.enableToolbarItems([ID_VALIDATE_MAPPING, ID_REJECT_MAPPING], this.canValidateMappings(selectedRecords));
  }

  public toolbarClick(args: ClickEventArgs): void {
    if (args.item.id === ID_REJECT_MAPPING) {
      this.validateOrRejectMappings(false);
    } else if (args.item.id === ID_VALIDATE_MAPPING) {
      this.validateOrRejectMappings(true);
    }
  }

  public canValidateMappings(mappings: MappingPendingCheck[]): boolean {
    return mappings.every((m) => m.submittedByEmail !== this.userEmail);
  }

  private validateOrRejectMappings(validated: boolean): void {
    const selectedRecords = this.grid?.getSelectedRecords() as MappingPendingCheck[];

    if (!selectedRecords || selectedRecords.length === 0) {
      return;
    }

    this.processingMessage = validated
      ? `Validating ${selectedRecords.length} mappings...`
      : `Rejecting ${selectedRecords.length} mappings...`;

    this.toastValidateMappingsAction?.show();

    combineLatest(
      selectedRecords.map((record) =>
        this.mappingsService.validateMapping$({
          mappingId: record.mappingId,
          mappingType: record.mappingType,
          validated
        })
      )
    ).subscribe({
      next: () => this.handleAfterExecute(true),
      error: () => this.handleAfterExecute(false)
    });
  }

  private handleAfterExecute(success: boolean): void {
    if (success) {
      this.mappingsPendingCheck$ = this.mappingsService.listMappingsPendingCheck$();
      this.processingMessage = `Mappings validation completed.`;

      setTimeout(() => {
        this.resetToast();
      }, 1500);
    } else {
      this.resetToast();
    }
  }

  private resetToast() {
    this.toastValidateMappingsAction?.hide();
    this.processingMessage = '';
  }
}
